import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { toast } from "sonner";
import { ChevronsUpDown, Check, AlertTriangle, Loader2 } from "lucide-react";

const PAGE_SIZE = 50;

// Código de Tributação Nacional (cTribNac) — Ajuste de Produto: NUNCA mais
// campo livre de 6 dígitos. Autocomplete/combobox pesquisável sobre a
// Lista de Serviços Nacional oficial (ANEXO I — ver
// backend/integrations/nfse_nacional/lista_servicos.py e
// docs/NFSE_NACIONAL.md). Busca é sempre no BACKEND (nunca confia só no
// frontend — o backend também valida na hora de salvar); aqui só filtra
// rapidamente enquanto o usuário digita.
//
// Editor completo — item 5 (correção de bug): o limite de página (50) NUNCA
// impede acesso ao restante dos 337 códigos oficiais — "Carregar mais"
// avança por `offset` até esgotar `total`, nunca corta a lista.
// Item 6 (correção de bug): descrições longas NUNCA esticam o popover
// horizontalmente — o código fica numa coluna curta/fixa, a descrição
// quebra linha (`whitespace-normal break-words`), sem depender de
// `truncate` num flex item sem `min-width:0` (a causa raiz do bug antigo).
export default function CodigoTributacaoNacionalCombobox({ value, info, onChange, testId = "nfse-cod-servico" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const debounceRef = useRef(null);

  const search = async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/config/fiscal/nfse/codigos-tributacao", { params: { q, limit: PAGE_SIZE, offset: 0 } });
      setResults(data.resultados || []);
      setTotal(data.total ?? (data.resultados || []).length);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/config/fiscal/nfse/codigos-tributacao", { params: { q: query, limit: PAGE_SIZE, offset: results.length } });
      setResults((prev) => [...prev, ...(data.resultados || [])]);
      setTotal(data.total ?? total);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open]);

  useEffect(() => {
    if (open && results.length === 0 && !query) search("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const restantes = Math.max(total - results.length, 0);

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full h-auto min-h-9 justify-between font-normal text-left py-2"
            data-testid={testId}
          >
            {value ? (
              <span className="min-w-0 flex-1 whitespace-normal break-words leading-snug" title={info?.descricao || ""}>
                <span className="font-mono text-xs text-slate-500 mr-1">{info?.codigo_exibicao || value}</span>
                <span>{info?.descricao || "(descrição indisponível)"}</span>
              </span>
            ) : (
              <span className="text-muted-foreground">Selecione o serviço...</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] max-w-[92vw] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Buscar por código (01.01.01) ou descrição..."
              value={query}
              onValueChange={setQuery}
              data-testid={`${testId}-busca`}
            />
            <CommandList>
              <CommandEmpty>{loading ? "Buscando..." : "Nenhum serviço encontrado."}</CommandEmpty>
              <CommandGroup>
                {results.map((r) => (
                  <CommandItem
                    key={r.codigo}
                    value={r.codigo}
                    onSelect={() => { onChange(r); setOpen(false); setQuery(""); }}
                    data-testid={`${testId}-opcao-${r.codigo}`}
                    className="items-start"
                  >
                    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${value === r.codigo ? "opacity-100" : "opacity-0"}`} />
                    <span className="font-mono text-xs shrink-0 text-slate-500 mt-0.5 w-[60px]">{r.codigo_exibicao}</span>
                    <span className="min-w-0 flex-1 whitespace-normal break-words leading-snug" title={r.descricao}>
                      {r.descricao}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
            {restantes > 0 && (
              <div className="border-t p-1.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs"
                  disabled={loadingMore}
                  onClick={loadMore}
                  data-testid={`${testId}-carregar-mais`}
                >
                  {loadingMore ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : null}
                  Carregar mais ({restantes} de {total} restantes)
                </Button>
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>
      {info && info.valido === false && (
        <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
          <AlertTriangle size={12} />
          Este código não consta mais na Lista de Serviços Nacional vigente — a configuração foi preservada,
          mas selecione um código atual antes de emitir.
        </p>
      )}
    </div>
  );
}
